import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDistanceToNowStrict } from "date-fns";

/**
 * Route mapping for notification types.
 * Each returns a path string. Backend already sets `type` + `data` on each
 * notification (data may include group_id, bill_id, friend_id).
 */
const routeFor = (n) => {
  const d = n.meta || n.data || {};
  switch (n.type) {
    case "settlement_received":
    case "settlement_sent":
    case "payment_received":
    case "payment_sent":
      return d.friend_id ? `/friends/${d.friend_id}` : "/friends";
    case "bill_added":
    case "bill_edited":
    case "bill_deleted":
      return d.group_id ? `/groups/${d.group_id}` : "/groups";
    case "member_joined":
    case "added_to_group":
    case "group_invite":
      return d.group_id ? `/groups/${d.group_id}` : "/groups";
    case "friend_added":
    case "friend_request":
      return d.friend_id ? `/friends/${d.friend_id}` : "/friends";
    case "recurring_due":
    case "subscription_due":
      return "/pro-dashboard";
    case "salary_due":
    case "income_reminder":
      return "/pro-dashboard";
    case "goal_milestone":
      return "/pro-dashboard";
    case "weekly_summary":
    case "insight":
      return "/pro-dashboard";
    default:
      return null;
  }
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      console.warn("Failed to load notifications:", e?.response?.status || e?.message);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch (e) { console.warn(e?.message); }
  };

  const markOne = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); }
    catch (e) { /* ignore — some backends only support mark-all */ }
  };

  const removeOne = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) { console.warn(e?.message); }
  };

  const openOne = async (n) => {
    if (!n.read) { await markOne(n); }
    const target = routeFor(n);
    setOpen(false);
    if (target) { nav(target); }
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-md hover:bg-foreground/5 flex items-center justify-center transition" data-testid="notification-bell-btn">
          <Bell className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full brand-bg" data-testid="notification-unread-dot" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-card border-border">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="text-sm font-medium">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs brand-text hover:underline" data-testid="notif-mark-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-foreground/50" data-testid="notif-empty">No notifications yet</div>
          ) : (
            items.map((n) => {
              const routable = !!routeFor(n);
              return (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-border text-sm flex items-start gap-2 ${n.read ? "opacity-60" : ""} ${routable ? "cursor-pointer hover:bg-foreground/5" : ""}`}
                  onClick={() => routable && openOne(n)}
                  data-testid="notif-item"
                >
                  {!n.read && <span className="h-2 w-2 rounded-full brand-bg mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{n.message}</div>
                    <div className="text-[10px] font-mono text-foreground/40 mt-1 uppercase tracking-wider">
                      {formatDistanceToNowStrict(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeOne(n); }}
                    className="h-7 w-7 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition flex items-center justify-center shrink-0"
                    aria-label="Delete notification"
                    data-testid={`notif-del-${n.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
